import { ThemeEvents } from '@theme/events';

// The theme emits cart:update before morphSection completes. Observe scoped DOM changes too.
(function (root) {
  'use strict';
  if (root.__MAFE_CBD_CART_LOADER) return;
  const configSelector = 'template[data-mafe-cbd-cart-config]';
  const assets = new Map();
  let controller, observer, timer, running = false, rerun = false, mounted = false;

  let enhancementBlocked = false;
  const enhancementFastCheckout = new Map();
  function restoreEnhancementPresentation() {
    for (const [element, hidden] of enhancementFastCheckout) element.hidden = hidden;
    enhancementFastCheckout.clear();
  }
  function setEnhancementBlocked(blocked) {
    enhancementBlocked = blocked;
    if (!blocked) { restoreEnhancementPresentation(); return; }
    document.querySelectorAll('.additional-checkout-buttons').forEach(element => {
      if (!enhancementFastCheckout.has(element)) enhancementFastCheckout.set(element, element.hidden);
      element.hidden = true;
    });
  }
  document.addEventListener('submit', event => {
    if (!enhancementBlocked || event.target?.id !== 'cart-form') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const host = document.querySelector('.cart-page__items');
    if (!host) return;
    let status = document.getElementById('mafe-enhancement-status');
    if (!status) { status = document.createElement('p'); status.id = 'mafe-enhancement-status'; status.setAttribute('role', 'status'); host.prepend(status); }
    status.textContent = 'Your meal and add-on cart could not be verified yet. Please refresh and review it before checkout.';
  }, true);

  function loadAsset(src, globalName) {
    if (root[globalName]) return Promise.resolve();
    if (assets.has(src)) return assets.get(src);
    const loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () => root[globalName] ? resolve() : reject(new Error('Meal cart asset did not initialize'));
      script.onerror = () => { script.remove(); reject(new Error('Meal cart asset could not load')); };
      document.head.append(script);
    }).catch(error => { assets.delete(src); throw error; });
    assets.set(src, loading);
    return loading;
  }

  function schedule() {
    if (!controller) return;
    clearTimeout(timer);
    timer = setTimeout(refresh, 75);
  }

  async function refresh() {
    if (!controller) return;
    if (running) { rerun = true; return; }
    running = true;
    try {
      const config = document.querySelector('cart-items-component ' + configSelector);
      if (!config || (config.dataset.hasMeals !== 'true' && !mounted)) return;
      // Once installed, the full guard owns validation even during widget remounts.
      if (config.dataset.hasMeals === 'true' && document.documentElement.dataset.mafeCartGuard !== 'true') setEnhancementBlocked(true);
      await loadAsset(config.dataset.orderSrc, 'MAFE_CBD_ORDER');
      // Loading this include never grants launch or fulfilment approval.
      if (!Object.values(root.MAFE_CBD_ORDER.launch).every(Boolean)) return;
      const policySrc = config.closest('cart-items-component')?.dataset.mafeCbdPolicySrc;
      if (!policySrc) throw new Error('Meal date policy source is missing');
      await loadAsset(policySrc, 'MAFE_CBD_FULFILMENT');
      if (!config.dataset.soupSrc) throw new Error('Meal soup policy source is missing');
      await loadAsset(config.dataset.soupSrc, 'MAFE_CBD_SOUP');
      if (!config.dataset.addonConfigSrc || !config.dataset.addonsSrc) throw new Error('Add-on catalog source is missing');
      await loadAsset(config.dataset.addonConfigSrc, 'MAFE_CBD_ADDON_CONFIG');
      await loadAsset(config.dataset.addonsSrc, 'MAFE_CBD_ADDONS');
      root.MAFE_CBD_BUILDER_URL = config.dataset.builderUrl || '';
      root.MAFE_CBD_CART_MANAGED = true;
      await loadAsset(config.dataset.cartSrc, 'MAFE_CBD_CART');
      if (!controller || !config.isConnected) { rerun = true; return; }
      mounted = true;
      restoreEnhancementPresentation(); // The full cart guard now owns fast-checkout presentation.
      await root.MAFE_CBD_CART.mount();
      if (document.documentElement.dataset.mafeCartGuard === 'true') { setEnhancementBlocked(false); document.getElementById('mafe-enhancement-status')?.remove(); }
    } catch (error) {
      console.error('Meal cart enhancement could not initialize.', error);
    } finally {
      running = false;
      if (rerun) { rerun = false; schedule(); }
    }
  }

  function touchesMainCart(node) {
    const element = node?.nodeType === 1 ? node : node?.parentElement;
    if (!element || element.closest?.('#mafe-meal-cart-groups')) return false;
    return Boolean(element.closest?.('cart-items-component')?.querySelector(configSelector)) ||
      Boolean(element.querySelector?.('cart-items-component ' + configSelector));
  }

  function connect() {
    if (controller) return;
    controller = new AbortController();
    const options = { signal: controller.signal };
    document.addEventListener(ThemeEvents.cartUpdate, schedule, options);
    document.addEventListener(ThemeEvents.discountUpdate, schedule, options);
    document.addEventListener('shopify:section:load', schedule, options);
    observer = new MutationObserver(changes => {
      if (changes.some(change => touchesMainCart(change.target) ||
          [...change.addedNodes, ...change.removedNodes].some(touchesMainCart))) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    root.addEventListener('pagehide', disconnect, options);
    schedule();
  }

  function disconnect() {
    controller?.abort();
    controller = undefined;
    observer?.disconnect();
    clearTimeout(timer);
    rerun = false;
  }

  root.__MAFE_CBD_CART_LOADER = { refresh: schedule, disconnect };
  root.addEventListener('pageshow', connect);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connect, { once: true });
  else connect();
})(window);


